import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import client from '../api/client'
import Spinner from '../components/Spinner'
import { useAuthStore } from '../store/authStore'

const MBTI_LIST = ['INTJ','INTP','ENTJ','ENTP','INFJ','INFP','ENFJ','ENFP','ISTJ','ISFJ','ESTJ','ESFJ','ISTP','ISFP','ESTP','ESFP']

const GAN_MAP: Record<string, string> = {
  '갑':'甲','을':'乙','병':'丙','정':'丁','무':'戊','기':'己','경':'庚','신':'辛','임':'壬','계':'癸'
}
const JI_MAP: Record<string, string> = {
  '자':'子','축':'丑','인':'寅','묘':'卯','진':'辰','사':'巳','오':'午','미':'未','신':'申','유':'酉','술':'戌','해':'亥'
}

function hanja(gan: string, ji: string) {
  return `${GAN_MAP[gan] || gan}${JI_MAP[ji] || ji}`
}

type SajuPreview = { year_gan: string; year_ji: string; month_gan: string; month_ji: string; day_gan: string; day_ji: string; time_gan?: string; time_ji?: string } | null

type CardData = {
  card_type_name: string | null
  card_type_desc: string | null
  day_gan: string
  element: string
  share_token: string | null
}

const ELEMENT_EMOJI: Record<string, string> = {
  목: '🌿', 화: '🔥', 토: '🏔️', 금: '⚙️', 수: '💧',
}
const ELEMENT_COLOR: Record<string, string> = {
  목: 'from-green-900/60 to-green-800/30 border-green-500/30',
  화: 'from-red-900/60 to-orange-800/30 border-red-500/30',
  토: 'from-yellow-900/60 to-amber-800/30 border-amber-500/30',
  금: 'from-slate-700/60 to-slate-600/30 border-slate-400/30',
  수: 'from-blue-900/60 to-blue-800/30 border-blue-500/30',
}

function StepIndicator({ step }: { step: number }) {
  if (step === 4) return null
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1,2,3].map((s) => (
        <div key={s} className={`w-3 h-3 rounded-full transition-colors ${step >= s ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border-default)]'}`} />
      ))}
    </div>
  )
}

export default function Register() {
  const navigate = useNavigate()
  const setUser = useAuthStore((s) => s.setUser)
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [sajuPreview, setSajuPreview] = useState<SajuPreview>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [card, setCard] = useState<CardData | null>(null)
  const [cardLoading, setCardLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const [form, setForm] = useState({
    email: '', password: '', confirm: '',
    nickname: '', gender: 'M', mbti: '', blood_type: '',
    birth_date: '', birth_time: '', birth_type: 'solar',
  })

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const inputCls = "w-full bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-lg px-4 py-2.5 text-[var(--color-text-primary)] placeholder-[var(--color-text-disabled)] focus:outline-none focus:border-[var(--color-border-accent)] transition-colors"
  const labelCls = "block text-sm text-[var(--color-text-secondary)] mb-1"
  const toggleBtn = (active: boolean) =>
    `flex-1 py-2.5 rounded-lg border text-sm font-medium transition-colors ${active ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)] font-bold' : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]'}`

  const nextStep = () => {
    setError('')
    if (step === 1) {
      if (!form.email || !form.password) { setError('이메일과 비밀번호를 입력해주세요.'); return }
      if (form.password.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return }
      if (form.password !== form.confirm) { setError('비밀번호가 일치하지 않습니다.'); return }
    }
    if (step === 2) {
      if (!form.nickname) { setError('닉네임을 입력해주세요.'); return }
    }
    setStep((s) => s + 1)
  }

  const fetchPreview = async (birth_date: string, birth_time: string, birth_type: string) => {
    if (!birth_date) return
    setPreviewLoading(true)
    try {
      const { data } = await client.post('/saju/preview', { birth_date, birth_time: birth_time || null, birth_type })
      setSajuPreview(data)
    } catch {
      setSajuPreview(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleBirthDateChange = (v: string) => {
    set('birth_date', v)
    if (v) fetchPreview(v, form.birth_time, form.birth_type)
  }

  const handleBirthTimeChange = (v: string) => {
    set('birth_time', v)
    if (form.birth_date) fetchPreview(form.birth_date, v, form.birth_type)
  }

  const handleBirthTypeChange = (v: string) => {
    set('birth_type', v)
    if (form.birth_date) fetchPreview(form.birth_date, form.birth_time, v)
  }

  const handleSubmit = async () => {
    if (!form.birth_date) { setError('생년월일을 입력해주세요.'); return }
    setError('')
    setLoading(true)
    try {
      // 1. 회원가입
      await client.post('/auth/register', {
        email: form.email,
        password: form.password,
        nickname: form.nickname,
        gender: form.gender,
        birth_date: form.birth_date,
        birth_time: form.birth_time || null,
        birth_type: form.birth_type,
        mbti: form.mbti || null,
        blood_type: form.blood_type || null,
      })

      // 2. 자동 로그인
      const { data: tokens } = await client.post('/auth/login', {
        email: form.email,
        password: form.password,
      })
      localStorage.setItem('access_token', tokens.access_token)
      localStorage.setItem('refresh_token', tokens.refresh_token)

      // 3. 유저 정보 로드
      const { data: userData } = await client.get('/users/me')
      setUser(userData)

      // 4. 완료 화면으로 전환 (카드는 비동기 로드)
      setStep(4)
      setCardLoading(true)
      try {
        const { data: cardData } = await client.get('/features/character-card')
        setCard(cardData)
      } catch {
        // 카드 로드 실패해도 완료 화면은 표시
      } finally {
        setCardLoading(false)
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || '회원가입에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyLink = async () => {
    if (!card?.share_token) return
    const url = `${window.location.origin}/api/share/${card.share_token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback: select
    }
  }

  // ── Step 4: 가입 완료 화면 ──
  if (step === 4) {
    const bgGradient = card ? (ELEMENT_COLOR[card.element] || ELEMENT_COLOR['토']) : 'from-slate-800/60 to-slate-700/30 border-slate-500/30'
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] py-10 px-4">
        <div className="w-full max-w-sm space-y-4">
          {/* 완료 헤더 */}
          <div className="text-center">
            <p className="text-5xl mb-3">🎉</p>
            <h1 className="text-2xl font-bold text-[var(--color-accent)]">단골 등록 완료!</h1>
            <p className="text-[var(--color-text-tertiary)] text-sm mt-1">{form.nickname}님, 환영합니다</p>
          </div>

          {/* 캐릭터 카드 */}
          <div className={`bg-gradient-to-b ${bgGradient} border rounded-2xl p-6 text-center space-y-4`}>
            {cardLoading ? (
              <div className="py-8 flex flex-col items-center gap-3">
                <Spinner size="md" />
                <p className="text-[var(--color-text-tertiary)] text-sm">럭키가 카드를 준비하는 중이에요</p>
              </div>
            ) : card ? (
              <>
                <div className="text-6xl">{ELEMENT_EMOJI[card.element] || '🌟'}</div>
                <div>
                  <p className="text-[var(--color-text-tertiary)] text-xs mb-1">{card.day_gan}일간 · {card.element}의 기운</p>
                  <h2 className="text-[var(--color-text-primary)] text-xl font-bold">{card.card_type_name}</h2>
                  {card.card_type_desc && (
                    <p className="text-[var(--color-text-secondary)] text-sm mt-2 leading-relaxed">{card.card_type_desc}</p>
                  )}
                </div>
                {card.share_token && (
                  <button
                    onClick={handleCopyLink}
                    className="w-full py-2.5 border border-[var(--color-accent)]/50 text-[var(--color-accent)] rounded-xl text-sm font-medium hover:bg-[var(--color-accent)]/10 transition-colors"
                  >
                    {copied ? '✅ 링크 복사됨!' : '🔗 카드 링크 공유하기'}
                  </button>
                )}
              </>
            ) : (
              <div className="py-4">
                <p className="text-4xl mb-2">🔮</p>
                <p className="text-[var(--color-text-tertiary)] text-sm">카드를 불러오지 못했어요.<br />나중에 마이페이지에서 확인해보세요.</p>
              </div>
            )}
          </div>

          {/* CTA 버튼 */}
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity"
          >
            🏮 상담 시작하기
          </button>
          <p className="text-center text-[var(--color-text-disabled)] text-xs">
            상담·궁합·오늘의 운세 등 모든 기능을 이용하실 수 있어요
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] py-8 px-4">
      <div className="w-full max-w-sm bg-[var(--color-surface)] rounded-2xl p-6 sm:p-8 shadow-xl border border-[var(--color-border-default)]">
        <div className="text-center mb-4">
          <p className="text-4xl mb-2">🏪</p>
          <h1 className="text-xl font-bold text-[var(--color-accent)]">단골 손님 등록</h1>
          <p className="text-[var(--color-text-tertiary)] text-xs mt-1">STEP {step} / 3 — {['계정 정보','나의 정보','사주 정보'][step-1]}</p>
        </div>

        <StepIndicator step={step} />

        {/* STEP 1 — 계정 정보 */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>📧 이메일</label>
              <input type="email" required className={inputCls} value={form.email}
                onChange={(e) => set('email', e.target.value)} autoComplete="email" />
            </div>
            <div>
              <label className={labelCls}>🔒 비밀번호 <span className="text-[var(--color-text-tertiary)]">(6자 이상)</span></label>
              <input type="password" required className={inputCls} value={form.password}
                onChange={(e) => set('password', e.target.value)} autoComplete="new-password" />
            </div>
            <div>
              <label className={labelCls}>🔒 비밀번호 확인</label>
              <input type="password" required className={inputCls} value={form.confirm}
                onChange={(e) => set('confirm', e.target.value)} autoComplete="new-password" />
            </div>
          </div>
        )}

        {/* STEP 2 — 나의 정보 */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>😊 닉네임</label>
              <input type="text" required className={inputCls} value={form.nickname}
                onChange={(e) => set('nickname', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>👤 성별</label>
              <div className="flex gap-3">
                <button type="button" onClick={() => set('gender', 'M')} className={toggleBtn(form.gender === 'M')}>남성</button>
                <button type="button" onClick={() => set('gender', 'F')} className={toggleBtn(form.gender === 'F')}>여성</button>
              </div>
            </div>
            <div>
              <label className={labelCls}>🔤 MBTI <span className="text-[var(--color-text-tertiary)]">(선택)</span></label>
              <select className={inputCls} value={form.mbti} onChange={(e) => set('mbti', e.target.value)}>
                <option value="">선택 안함</option>
                {MBTI_LIST.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>🩸 혈액형 <span className="text-[var(--color-text-tertiary)]">(선택)</span></label>
              <div className="flex flex-wrap gap-2">
                {['', 'A', 'B', 'O', 'AB'].map((v) => (
                  <button key={v} type="button" onClick={() => set('blood_type', v)}
                    className={toggleBtn(form.blood_type === v)}>
                    {v || '?'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — 사주 정보 */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>📅 생년월일</label>
              <input type="date" required className={inputCls} value={form.birth_date}
                onChange={(e) => handleBirthDateChange(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>☀️ 양력 / 음력</label>
              <div className="flex gap-3">
                <button type="button" onClick={() => handleBirthTypeChange('solar')} className={toggleBtn(form.birth_type === 'solar')}>양력</button>
                <button type="button" onClick={() => handleBirthTypeChange('lunar')} className={toggleBtn(form.birth_type === 'lunar')}>음력</button>
              </div>
            </div>
            <div>
              <label className={labelCls}>🕐 태어난 시간 <span className="text-[var(--color-text-tertiary)]">(선택)</span></label>
              <input type="time" className={inputCls} value={form.birth_time}
                onChange={(e) => handleBirthTimeChange(e.target.value)} />
              <p className="text-[var(--color-text-disabled)] text-xs mt-1">💡 시간을 모르면 비워두세요. 사주육자로 계산해요.</p>
            </div>

            {/* 사주 미리보기 */}
            {form.birth_date && (
              <div className="bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-xl px-4 py-3">
                <p className="text-[var(--color-text-tertiary)] text-xs mb-2">미리보기</p>
                {previewLoading ? (
                  <div className="flex justify-center py-2"><Spinner size="sm" /></div>
                ) : sajuPreview ? (
                  <div className="text-[var(--color-accent)] text-sm font-medium">
                    {hanja(sajuPreview.year_gan, sajuPreview.year_ji)}년{' '}
                    {hanja(sajuPreview.month_gan, sajuPreview.month_ji)}월{' '}
                    {hanja(sajuPreview.day_gan, sajuPreview.day_ji)}일
                    {sajuPreview.time_gan && sajuPreview.time_ji && (
                      <> {hanja(sajuPreview.time_gan, sajuPreview.time_ji)}시</>
                    )}
                    <p className="text-[var(--color-text-tertiary)] text-xs mt-1">{sajuPreview.day_gan}토 일간</p>
                  </div>
                ) : (
                  <p className="text-[var(--color-text-disabled)] text-xs">생년월일을 입력하면 사주가 표시돼요</p>
                )}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 rounded-xl px-4 py-2.5 text-[var(--color-error)] text-sm mt-4">{error}</div>
        )}

        {/* 버튼 */}
        <div className={`flex gap-3 mt-6 ${step > 1 ? '' : ''}`}>
          {step > 1 && (
            <button onClick={() => setStep((s) => s - 1)}
              className="flex-1 py-2.5 border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors">
              ← 이전
            </button>
          )}
          {step < 3 ? (
            <button onClick={nextStep}
              className="flex-1 py-2.5 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity">
              다음 →
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading}
              className="flex-1 py-2.5 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-60 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity flex items-center justify-center gap-2">
              {loading ? <><Spinner size="sm" /> 등록 중...</> : '🎉 단골 등록 완료!'}
            </button>
          )}
        </div>

        <p className="text-center text-[var(--color-text-tertiary)] text-sm mt-4">
          이미 계정이 있으신가요?{' '}
          <Link to="/login" className="text-[var(--color-accent)] hover:underline">로그인</Link>
        </p>
      </div>
    </div>
  )
}
